'use client'

import { createContext, useCallback, useContext, useMemo, useState, ReactNode, useEffect } from 'react'
import { ReferralModal } from '@/features/referral/referral-modal'
import {
    MODAL_QUERY_PARAM_KEY,
    MODAL_TYPES,
    parseModalParam,
    formatModalParam,
    guardIsReferralModal,
} from '@/setup/routing/modal-route'
import { useQueryParams } from '@/utils/url/use-query-params'
import { noOp } from '@/utils/functions/no-op'

export interface IReferralModalContext {
    isOpen: boolean
    openReferralModal: () => void
    closeReferralModal: () => void
}

const ReferralModalContext = createContext<IReferralModalContext>({
    isOpen: false,
    openReferralModal: noOp,
    closeReferralModal: noOp,
})

interface IReferralModalProviderProps {
    children: ReactNode
}

export function ReferralModalProvider({ children }: IReferralModalProviderProps) {
    const { params, getParam, setParam, deleteParam } = useQueryParams<{ [MODAL_QUERY_PARAM_KEY]: string }>()

    const isReferralInUrl = useMemo(
        () => guardIsReferralModal(parseModalParam(params[MODAL_QUERY_PARAM_KEY])),
        [params]
    )

    const [isOpen, setIsOpen] = useState(() => guardIsReferralModal(parseModalParam(getParam(MODAL_QUERY_PARAM_KEY))))

    useEffect(() => {
        setIsOpen(isReferralInUrl)
    }, [isReferralInUrl])

    const openReferralModal = useCallback(() => {
        setIsOpen(true)
        setParam(MODAL_QUERY_PARAM_KEY, formatModalParam(MODAL_TYPES.Referral))
    }, [setParam])

    const closeReferralModal = useCallback(() => {
        setIsOpen(false)
        deleteParam(MODAL_QUERY_PARAM_KEY)
    }, [deleteParam])

    return (
        <ReferralModalContext.Provider
            value={{
                isOpen,
                openReferralModal,
                closeReferralModal,
            }}
        >
            {children}
            <ReferralModal
                open={isOpen}
                onOpenChange={(open) => {
                    if (!open) closeReferralModal()
                }}
            />
        </ReferralModalContext.Provider>
    )
}

export function useReferralModal() {
    return useContext(ReferralModalContext)
}
